import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { api } from "../../utils/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";

/* ---------------------------------------------
   STATUS CONFIG (Single Source of Truth)
--------------------------------------------- */
const STATUS_OPTIONS = [
  {
    label: "All Status",
    value: "all",
  },
  {
    label: "Application Received",
    value: "Application Received, Pending for Review",
  },
  {
    label: "Additional Information Requested",
    value: "Additional Information Requested",
  },
  {
    label: "Additional Information Received",
    value: "Additional Information Received",
  },
  {
    label: "Review Passed - Pending Submission",
    value: "Review Passed, Pending for Submission",
  },
  {
    label: "Submission Done – Awaiting Visa",
    value: "Submission Done, Awaiting Visa Status",
  },
  {
    label: "Visa Approved",
    value: "Visa Approved",
  },
  {
    label: "Visa Rejected",
    value: "Visa Rejected",
  },
  {
    label: "Draft",
    value: "Draft",
  },
];

export default function ApplicationManagement() {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const { showToast } = useToast();

  const role = admin?.role || "admin";
  const isOperator = role === "operator";

  const [applications, setApplications] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  /* ---------------------------------------------
     LOAD DATA
  --------------------------------------------- */
  useEffect(() => {
    fetchApplications();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    filterApplications();
    // eslint-disable-next-line
  }, [applications, searchTerm, statusFilter]);

  const fetchApplications = async () => {
    try {
      const params = {};
      if (isOperator) params.mine_only = true;
      const res = await api.adminGetApplications(params);
      setApplications(res.data || []);
    } catch {
      showToast("Failed to load applications", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------
     FILTER LOGIC
  --------------------------------------------- */
  const filterApplications = () => {
    let data = [...applications];

    if (statusFilter && statusFilter !== "all") {
      data = data.filter((app) => app.status === statusFilter);
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      data = data.filter(
        (app) =>
          app.id.toLowerCase().includes(q) ||
          app.user?.email?.toLowerCase().includes(q) ||
          app.user?.name?.toLowerCase().includes(q)
      );
    }

    setFilteredApps(data);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      "Application Received, Pending for Review": {
        color: "bg-amber-50 text-amber-700 border-amber-200",
        icon: "⏳",
      },
      "Additional Information Requested": {
        color: "bg-orange-50 text-orange-700 border-orange-200",
        icon: "📝",
      },
      "Additional Information Received": {
        color: "bg-indigo-50 text-indigo-700 border-indigo-200",
        icon: "✓",
      },
      "Submission Done, Awaiting Visa Status": {
        color: "bg-purple-50 text-purple-700 border-purple-200",
        icon: "⏱️",
      },
      "Visa Approved": {
        color: "bg-emerald-50 text-emerald-700 border-emerald-200",
        icon: "✅",
      },
      "Visa Rejected": {
        color: "bg-red-50 text-red-700 border-red-200",
        icon: "❌",
      },
      draft: {
        color: "bg-slate-50 text-slate-600 border-slate-200",
        icon: "📄",
      },
    };

    const config = statusConfig[status] || statusConfig.draft;
    const label =
      STATUS_OPTIONS.find((s) => s.value === status)?.label || status;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border ${config.color}`}
      >
        <span>{config.icon}</span>
        {label}
      </span>
    );
  };

  // Stats for quick overview
  const stats = {
    total: applications.length,
    pending: applications.filter(
      (a) => a.status === "Application Received, Pending for Review"
    ).length,
    approved: applications.filter((a) => a.status === "Visa Approved").length,
    rejected: applications.filter((a) => a.status === "Visa Rejected").length,
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-3 border-red-600 border-t-transparent rounded-full mx-auto mb-3"></div>
            <p className="text-sm text-gray-600 font-medium">
              Loading applications...
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  /* ---------------------------------------------
     UI
  --------------------------------------------- */
  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* PAGE HEADER */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Application Management
              </h1>
            </div>
          </div>
        </div>

        {/* STATS CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <div className="bg-white hover:bg-blue-50 border border-gray-200 rounded-lg p-4 cursor-pointer transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase">
                  Total
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.total}
                </p>
              </div>
              <div className="text-2xl">📊</div>
            </div>
          </div>

          <div className="bg-white hover:bg-amber-50 border border-gray-200 rounded-lg p-4 cursor-pointer transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase">
                  Pending
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.pending}
                </p>
              </div>
              <div className="text-2xl">⏳</div>
            </div>
          </div>

          <div className="bg-white hover:bg-emerald-50 border border-gray-200 rounded-lg p-4 cursor-pointer transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase">
                  Approved
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.approved}
                </p>
              </div>
              <div className="text-2xl">✅</div>
            </div>
          </div>

          <div className="bg-white hover:bg-red-50 border border-gray-200 rounded-lg p-4 cursor-pointer transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase">
                  Rejected
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.rejected}
                </p>
              </div>
              <div className="text-2xl">❌</div>
            </div>
          </div>
        </div>

        {/* FILTERS */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <svg
                className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <Input
                placeholder="Search by ID, Email, or Agent Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* TABLE */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200 bg-gray-50/50">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">
                Applications ({filteredApps.length})
              </h3>
            </div>
          </div>

          {filteredApps.length ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Application ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Agent
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Visa Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Assigned To
                    </th>
                    {role === "admin" || role === "operator" ? (
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Actions
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredApps.map((app) => (
                    <tr
                      key={app.id}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(app.submitted_at || app.created_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-mono text-gray-700">
                          {app.id}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">
                          {app.user?.name || "Unknown"}
                        </span>
                        <div className="text-xs text-gray-500">
                          {app.user?.email || "No email"}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                         <div className="text-sm font-medium text-gray-900">
                          {app.visa_type?.category || "N/A"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {app.visa_type?.name  || "N/A"}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getStatusBadge(app.status)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {app.assigned_to_admin ? (
                          <span className="text-sm text-gray-700">
                            {app.assigned_to_admin.name}
                          </span>
                        ) : (
                          <span className="text-sm italic text-gray-400">
                            Unassigned
                          </span>
                        )}
                      </td>
                      {role === "admin" || role === "operator" ? (
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              onClick={() =>
                                navigate(`/admin/applications/${app.id}`)
                              }
                              className="inline-flex items-center justify-center w-8 h-8 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                              title="View Application"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                            </button>
                          </div>
                        </td>
                      ): null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 text-2xl">
                📋
              </div>
              <h4 className="text-sm text-gray-900 font-semibold mb-1">
                No applications found
              </h4>
              <p className="text-xs text-gray-500 mb-4">
                {searchTerm || statusFilter
                  ? "Try adjusting your filters"
                  : "Applications will appear here once submitted"}
              </p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}